<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1400px] mx-auto">

    <!-- ============ HERO ============ -->
    <div class="relative overflow-hidden rounded-3xl bg-neutral-900 text-white px-6 sm:px-8 py-7 mb-6 shadow-lg">
      <div class="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl"></div>
      <div class="pointer-events-none absolute right-10 bottom-0 opacity-[0.07]"><mat-icon class="!text-[160px] !h-[160px] !w-[160px]">local_shipping</mat-icon></div>

      <div class="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div class="max-w-xl">
          <div class="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">local_shipping</mat-icon> Products
          </div>
          <h1 class="mt-2 text-3xl font-bold tracking-tight">Delivery Terms</h1>
          <p class="mt-2 text-sm text-white/60 leading-relaxed">The delivery estimate each variant shows on its product page. Checkout combines them to give the customer one delivery window for the whole order.</p>
        </div>
        <button type="button"
          class="group self-start lg:self-auto flex items-center gap-2.5 pl-2.5 pr-5 py-2.5 rounded-2xl bg-amber-400 text-neutral-900 font-bold text-sm shadow-lg shadow-amber-500/20 hover:bg-amber-300 hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-4 focus:ring-amber-400/40"
          (click)="openCreate()">
          <span class="h-8 w-8 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform duration-200 group-hover:rotate-90">
            <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
          </span>
          New delivery term
        </button>
      </div>

      <div class="relative mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
        @for (s of [
          { label: 'Terms', value: counts.total, icon: 'list_alt' },
          { label: 'In use', value: counts.used, icon: 'check_circle' },
          { label: 'Unused', value: counts.unused, icon: 'radio_button_unchecked' },
          { label: 'Variants covered', value: totalVariants, icon: 'inventory_2' }
        ]; track s.label) {
        <div class="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold text-white/50 uppercase tracking-wide">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ s.icon }}</mat-icon>{{ s.label }}
          </div>
          <div class="mt-1 text-2xl font-bold tabular-nums">{{ loading ? '–' : (s.value | number) }}</div>
        </div>
        }
      </div>
    </div>

    <!-- ============ TOOLBAR ============ -->
    <div class="sticky top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mb-4 bg-gray-50/90 backdrop-blur">
      <div class="flex flex-col sm:flex-row sm:items-center gap-3">
        <div class="relative flex-1">
          <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
          <input type="text" placeholder="Search by description or slug…"
            class="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
            [(ngModel)]="search" />
          @if (search) {
          <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="search = ''" aria-label="Clear search">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
          </button>
          }
        </div>
        <div class="flex bg-white border border-gray-200 rounded-xl p-1 gap-1 shadow-sm self-start">
          @for (f of [{ key: 'ALL', label: 'All', n: counts.total }, { key: 'USED', label: 'In use', n: counts.used }, { key: 'UNUSED', label: 'Unused', n: counts.unused }]; track f.key) {
          <button class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            [ngClass]="filter === f.key ? 'bg-neutral-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'" (click)="filter = $any(f.key)">
            {{ f.label }} <span class="tabular-nums" [ngClass]="filter === f.key ? 'text-amber-400' : 'text-gray-400'">{{ f.n }}</span>
          </button>
          }
        </div>
      </div>
    </div>

    @if (loadError) {
      <div class="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between">
        <span class="flex items-center gap-2"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">cloud_off</mat-icon>Could not load delivery terms from the server.</span>
        <button class="text-xs uppercase font-semibold underline" (click)="load()">Try again</button>
      </div>
    }

    <!-- ============ LIST ============ -->
    @if (loading) {
      <div class="flex flex-col gap-2.5">
        @for (i of [1, 2, 3, 4, 5, 6]; track i) {
        <div class="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 px-5 py-4 animate-pulse">
          <div class="h-12 w-14 rounded-2xl bg-gray-100"></div>
          <div class="flex-1"><div class="h-4 w-1/3 rounded bg-gray-100"></div><div class="mt-2 h-3 w-1/5 rounded bg-gray-100"></div></div>
          <div class="hidden md:block h-2 w-1/4 rounded-full bg-gray-100"></div>
          <div class="hidden md:block h-10 w-10 rounded-full bg-gray-100"></div>
        </div>
        }
      </div>
    } @else {
      @for (g of groups; track g.key) {
      <section class="mb-7 last:mb-0">
        <!-- Group divider -->
        <div class="flex items-center gap-3 mb-3 px-1">
          <span class="h-7 w-7 rounded-lg flex items-center justify-center"
            [ngClass]="g.key === 'RANGE' ? 'bg-amber-100 text-amber-700' : g.key === 'CALL' ? 'bg-sky-100 text-sky-700' : 'bg-gray-200 text-gray-600'">
            <mat-icon class="!text-[16px] !h-4 !w-4">{{ g.icon }}</mat-icon>
          </span>
          <span class="text-sm font-bold text-gray-900">{{ g.title }}</span>
          <span class="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-500 tabular-nums">{{ g.items.length }}</span>
          <span class="hidden lg:inline text-xs text-gray-400">{{ g.hint }}</span>
          <span class="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent"></span>
          @if (g.key === 'RANGE') { <span class="hidden md:inline text-[11px] text-gray-400 tabular-nums">scale 1–{{ maxDays }} days</span> }
        </div>

        <div class="flex flex-col gap-2.5">
          @for (t of g.items; track t.id) {
          <div role="button" tabindex="0" (click)="view(t)" (keydown.enter)="view(t)"
            class="group relative grid grid-cols-[auto_minmax(0,1fr)_auto] md:grid-cols-[auto_minmax(0,1.6fr)_minmax(0,1.3fr)_minmax(150px,0.9fr)_auto] gap-x-4 gap-y-3 items-center rounded-2xl bg-white border px-4 sm:px-5 py-3.5 cursor-pointer transition-all duration-200 hover:-translate-y-px hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100"
            [ngClass]="selected?.id === t.id ? 'border-amber-300 ring-4 ring-amber-100/80' : 'border-gray-100 hover:border-gray-200'">

            <!-- Badge -->
            @if (rangeOf(t); as r) {
              <span class="h-12 min-w-[56px] px-2.5 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-neutral-900 flex flex-col items-center justify-center shadow-sm shadow-amber-200">
                <span class="text-base font-extrabold leading-none tabular-nums">{{ r[0] }}–{{ r[1] }}</span>
                <span class="text-[9px] font-bold uppercase tracking-[0.12em] opacity-70 mt-0.5">days</span>
              </span>
            } @else if (g.key === 'CALL') {
              <span class="h-12 w-14 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 text-white flex items-center justify-center shadow-sm shadow-sky-200"><mat-icon class="!text-[22px] !h-[22px] !w-[22px]">call</mat-icon></span>
            } @else {
              <span class="h-12 w-14 rounded-2xl bg-gradient-to-br from-gray-500 to-gray-700 text-white flex items-center justify-center shadow-sm shadow-gray-200"><mat-icon class="!text-[22px] !h-[22px] !w-[22px]">storefront</mat-icon></span>
            }

            <!-- Description -->
            <div class="min-w-0">
              <div class="text-[15px] font-semibold text-gray-900 truncate" [title]="t.deliveryTermDesc">{{ t.deliveryTermDesc }}</div>
              <div class="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                <span class="font-mono px-1.5 py-px rounded-md bg-gray-100 text-gray-500">{{ t.deliveryTermSlug }}</span>
                @if (g.key === 'OTHER') { <span class="inline-flex items-center gap-0.5 px-1.5 py-px rounded-md bg-amber-50 text-amber-700 font-medium"><mat-icon class="!text-[12px] !h-3 !w-3">info</mat-icon>No estimate</span> }
                @if (g.key === 'CALL') { <span class="px-1.5 py-px rounded-md bg-sky-50 text-sky-700 font-medium">Call to arrange</span> }
                @if (!t.variants) { <span class="px-1.5 py-px rounded-md bg-gray-50 border border-dashed border-gray-200 text-gray-400 font-medium">Unused</span> }
              </div>
            </div>

            <!-- Actions (top right on phones, last on desktop) -->
            <div class="md:order-last flex items-center gap-0.5 rounded-xl bg-gray-50 border border-gray-100 p-0.5 md:opacity-0 md:translate-x-1 md:group-hover:opacity-100 md:group-hover:translate-x-0 md:group-focus-within:opacity-100 md:group-focus-within:translate-x-0 transition-all duration-200"
              [class.md:opacity-100]="selected?.id === t.id" [class.md:translate-x-0]="selected?.id === t.id">
              <button type="button" class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-gray-900 hover:shadow-sm flex items-center justify-center" matTooltip="View" (click)="$event.stopPropagation(); view(t)" aria-label="View"><mat-icon class="!text-[17px] !h-[17px] !w-[17px]">visibility</mat-icon></button>
              <button type="button" class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-gray-900 hover:shadow-sm flex items-center justify-center" matTooltip="Edit" (click)="openEdit(t, $event)" aria-label="Edit"><mat-icon class="!text-[17px] !h-[17px] !w-[17px]">edit</mat-icon></button>
              <span [matTooltip]="deleteTooltip(t)">
                <button type="button" class="h-8 w-8 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none" [disabled]="!canDelete || t.variants > 0" (click)="remove(t, $event)" aria-label="Delete"><mat-icon class="!text-[17px] !h-[17px] !w-[17px]">delete</mat-icon></button>
              </span>
            </div>

            <!-- Days on the shared scale -->
            <div class="col-span-3 md:col-span-1">
              @if (rangeOf(t); as r) {
                <div class="relative pt-4">
                  <span class="absolute top-0 -translate-x-1/2 text-[10px] font-bold text-amber-700 tabular-nums" [style.left.%]="((r[0] - 0.5) / maxDays) * 100">{{ r[0] }}</span>
                  @if (r[1] !== r[0]) {
                    <span class="absolute top-0 -translate-x-1/2 text-[10px] font-bold text-amber-700 tabular-nums" [style.left.%]="((r[1] - 0.5) / maxDays) * 100">{{ r[1] }}</span>
                  }
                  <div class="relative h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    @for (tick of scaleTicks.slice(1, 4); track tick) {
                      <span class="absolute inset-y-0 w-px bg-white" [style.left.%]="tick * 100"></span>
                    }
                    <div class="absolute inset-y-0 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 shadow-[0_0_0_1px_rgba(245,158,11,0.25)]"
                      [style.left.%]="((r[0] - 1) / maxDays) * 100" [style.width.%]="Math.max(((r[1] - r[0] + 1) / maxDays) * 100, 3)"></div>
                  </div>
                </div>
              } @else {
                <div class="pt-4"><div class="h-2.5 rounded-full bg-[repeating-linear-gradient(45deg,#f3f4f6_0_6px,#fff_6px_12px)] border border-gray-100"></div></div>
              }
            </div>

            <!-- Usage ring -->
            <div class="col-span-3 md:col-span-1 flex items-center gap-3">
              <div class="relative h-11 w-11 flex-shrink-0" [matTooltip]="t.variants ? share(t) + '% of all variants' : 'Not used'">
                <svg viewBox="0 0 36 36" class="h-11 w-11 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f3f4f6" stroke-width="3.2" />
                  @if (t.variants) {
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke-width="3.2" stroke-linecap="round"
                      [attr.stroke]="g.key === 'CALL' ? '#0284c7' : g.key === 'OTHER' ? '#6b7280' : '#f59e0b'"
                      [attr.stroke-dasharray]="Math.max(share(t), 2) + ' 100'" />
                  }
                </svg>
                <span class="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums" [ngClass]="t.variants ? 'text-gray-800' : 'text-gray-300'">{{ share(t) }}%</span>
              </div>
              <div class="min-w-0 leading-tight">
                <div class="text-sm"><span class="font-bold text-gray-900 tabular-nums">{{ t.variants | number }}</span> <span class="text-gray-500">variant{{ t.variants === 1 ? '' : 's' }}</span></div>
                <div class="text-[11px] text-gray-400">{{ t.products | number }} product{{ t.products === 1 ? '' : 's' }}</div>
              </div>
            </div>
          </div>
          }
        </div>
      </section>
      } @empty {
        <div class="rounded-3xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
          <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">search_off</mat-icon></span>
          <p class="mt-3 text-sm font-semibold text-gray-700">No delivery terms match</p>
          <p class="text-xs text-gray-400">Try another search or filter</p>
          @if (search || filter !== 'ALL') {
            <button type="button" class="mt-4 text-xs font-semibold text-amber-700 hover:underline" (click)="search = ''; filter = 'ALL'">Clear search and filter</button>
          }
        </div>
      }
    }
  </div>
  </div>

  <!-- ============ VIEW (slide-over) ============ -->
  @if (selected) {
  <div class="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px] dt-fade" (click)="selected = null"></div>
  <aside class="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col dt-slide" role="dialog" aria-modal="true" aria-label="Delivery term">
    <div class="relative overflow-hidden bg-neutral-900 text-white px-6 pt-6 pb-5">
      <div class="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-400/20 blur-2xl"></div>
      <div class="relative flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">Delivery term</div>
          @if (rangeOf(selected); as r) {
            <div class="mt-2 text-3xl font-bold tabular-nums">{{ r[0] }}–{{ r[1] }} <span class="text-base font-medium text-white/50">working days</span></div>
          } @else {
            <div class="mt-2 text-2xl font-bold capitalize">{{ selected.deliveryTermSlug === 'call' ? 'Call to arrange' : selected.deliveryTermSlug }}</div>
          }
          <p class="mt-1 text-sm text-white/70">{{ selected.deliveryTermDesc }}</p>
        </div>
        <button type="button" class="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0" (click)="selected = null" aria-label="Close"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon></button>
      </div>
      <div class="relative mt-5 grid grid-cols-3 gap-2">
        <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Variants</div><div class="text-lg font-bold tabular-nums">{{ selected.variants | number }}</div></div>
        <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Products</div><div class="text-lg font-bold tabular-nums">{{ selected.products | number }}</div></div>
        <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Slug</div><div class="text-lg font-bold font-mono truncate">{{ selected.deliveryTermSlug }}</div></div>
      </div>
      <div class="relative mt-4 flex gap-2">
        <button type="button" class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-400 text-neutral-900 text-xs font-bold hover:bg-amber-300" (click)="openEdit(selected)"><mat-icon class="!text-[16px] !h-4 !w-4">edit</mat-icon>Edit</button>
        @if (canDelete && !selected.variants) {
          <button type="button" class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/10 text-white text-xs font-semibold hover:bg-red-600" (click)="remove(selected)"><mat-icon class="!text-[16px] !h-4 !w-4">delete</mat-icon>Delete</button>
        }
      </div>
    </div>

    <div class="flex-1 min-h-0 flex flex-col px-6 py-5">
      <div class="flex items-center justify-between mb-3">
        <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500">Variants on this term</span>
        @if (variants.length) { <span class="text-xs text-gray-400 tabular-nums">{{ visibleVariants.length }} of {{ variants.length }}</span> }
      </div>
      @if (variantsLoading) {
        <div class="flex-1 flex items-center justify-center"><mat-spinner diameter="26"></mat-spinner></div>
      } @else if (!variants.length) {
        <div class="rounded-2xl bg-gray-50 py-10 text-center">
          <mat-icon class="!text-[30px] !h-[30px] !w-[30px] text-gray-300">inventory_2</mat-icon>
          <p class="mt-1 text-sm text-gray-500">No variants use this term</p>
          @if (canDelete) { <p class="text-xs text-gray-400">It can be deleted safely</p> }
        </div>
      } @else {
        <div class="relative mb-3">
          <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 !text-gray-400 !text-[18px] !h-[18px] !w-[18px] pointer-events-none">search</mat-icon>
          <input type="text" [(ngModel)]="variantSearch" placeholder="Filter by SKU or product"
            class="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm outline-none focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100" />
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto -mx-2 px-2">
          @for (v of visibleVariants; track v.productVariantId) {
          <a class="group/item flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-amber-50/70 transition-colors" [routerLink]="['/products/edit', v.productSlug]" target="_blank" rel="noopener">
            <span class="h-8 w-8 rounded-lg bg-gray-100 text-gray-400 group-hover/item:bg-amber-100 group-hover/item:text-amber-700 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[16px] !h-4 !w-4">sell</mat-icon></span>
            <div class="min-w-0 flex-1">
              <div class="font-mono text-xs font-semibold text-gray-900 truncate">{{ v.sku }}</div>
              <div class="text-[11px] text-gray-500 truncate">{{ v.productName }}</div>
            </div>
            @if (!v.enabled) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Disabled</span> }
            <mat-icon class="!text-[16px] !h-4 !w-4 text-gray-300 group-hover/item:text-amber-600">open_in_new</mat-icon>
          </a>
          } @empty {
          <div class="py-8 text-center text-sm text-gray-400">No variants match</div>
          }
        </div>
      }
    </div>
  </aside>
  }

</app-main-layout>
